import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { formatPhone } from '../utils/formatPhone';

// ── DB Connector Form ─────────────────────────────────────────────────────────
function ConnectorForm({ initial, onSave, onCancel }) {
  const isNew = !initial;
  const [form, setForm] = useState({
    label:          initial?.label || '',
    type:           initial?.type || 'postgres',
    host:           initial?.host || '',
    port:           initial?.port || '',
    database:       initial?.database || '',
    description:    initial?.description || '',
    allowedTables:  initial?.allowedTables?.join(', ') || '',
    username:       '',
    password:       '',
    uri:            '',
    enabled:        initial?.enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const isPostgres = form.type === 'postgres';

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        label:         form.label,
        type:          form.type,
        host:          form.host,
        port:          form.port || undefined,
        database:      form.database,
        description:   form.description,
        enabled:       form.enabled,
        allowedTables: form.allowedTables.split(',').map(s => s.trim()).filter(Boolean),
      };

      // Only include credentials if provided
      const creds = {};
      if (isPostgres) {
        if (form.username) creds.username = form.username;
        if (form.password) creds.password = form.password;
      } else {
        if (form.uri) creds.uri = form.uri;
      }
      if (Object.keys(creds).length > 0) payload.credentials = creds;

      let data;
      if (isNew) {
        ({ data } = await api.post('/api/support-db', payload));
      } else {
        ({ data } = await api.put(`/api/support-db/${initial._id}`, payload));
      }
      onSave(data.connector);
    } catch (err) {
      alert(err.message || 'Save failed');
    }
    setSaving(false);
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/api/support-db/${initial._id}/test`);
      setTestResult({ ok: true, msg: data.result?.message || 'Connection successful' });
    } catch (err) {
      setTestResult({ ok: false, msg: err.message });
    }
    setTesting(false);
  }

  return (
    <form onSubmit={handleSave} style={s.connectorForm}>
      <div style={s.formRow}>
        <div style={{ flex: 2 }}>
          <label style={s.label}>Label *</label>
          <input required value={form.label} onChange={e => set('label', e.target.value)} style={s.input} placeholder="e.g. LMS Database" />
        </div>
        <div style={{ flex: 1 }}>
          <label style={s.label}>Type *</label>
          <select value={form.type} onChange={e => set('type', e.target.value)} style={s.input} disabled={!isNew}>
            <option value="postgres">PostgreSQL</option>
            <option value="mongodb">MongoDB</option>
          </select>
        </div>
      </div>

      {isPostgres ? (
        <>
          <div style={s.formRow}>
            <div style={{ flex: 2 }}>
              <label style={s.label}>Host *</label>
              <input required={isNew} value={form.host} onChange={e => set('host', e.target.value)} style={s.input} placeholder="db.example.com" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Port</label>
              <input value={form.port} onChange={e => set('port', e.target.value)} style={s.input} placeholder="5432" type="number" />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={s.label}>Database Name *</label>
            <input required={isNew} value={form.database} onChange={e => set('database', e.target.value)} style={s.input} placeholder="mydb" />
          </div>
          <div style={s.formRow}>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Username {!isNew && '(leave blank to keep)'}</label>
              <input value={form.username} onChange={e => set('username', e.target.value)} style={s.input} placeholder="db_user" autoComplete="off" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Password {!isNew && '(leave blank to keep)'}</label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)} style={s.input} placeholder="••••••••" autoComplete="new-password" />
            </div>
          </div>
        </>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <label style={s.label}>Connection URI {!isNew && '(leave blank to keep)'}</label>
          <input value={form.uri} onChange={e => set('uri', e.target.value)} style={s.input} placeholder="mongodb+srv://user:pass@cluster.mongodb.net/db" />
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <label style={s.label}>Allowed Collections / Tables *</label>
        <input required={isNew} value={form.allowedTables} onChange={e => set('allowedTables', e.target.value)} style={s.input} placeholder="users, enrollments, courses (comma-separated)" />
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>The bot can only query these tables/collections.</div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={s.label}>Description (shown to the bot)</label>
        <input value={form.description} onChange={e => set('description', e.target.value)} style={s.input} placeholder="Contains student enrollment and fee records" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <input type="checkbox" id="enabled" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
        <label htmlFor="enabled" style={{ fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}>Enabled</label>
      </div>

      {testResult && (
        <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, background: testResult.ok ? '#052e1620' : '#1f0a0a', color: testResult.ok ? '#22c55e' : '#f87171', border: `1px solid ${testResult.ok ? '#22c55e30' : '#ef444430'}` }}>
          {testResult.ok ? 'Connected: ' : 'Failed: '}{testResult.msg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={saving} style={{ ...s.btn, background: '#3b82f6' }}>
          {saving ? 'Saving...' : (isNew ? 'Add Connector' : 'Save Changes')}
        </button>
        {!isNew && (
          <button type="button" onClick={handleTest} disabled={testing} style={{ ...s.btn, background: '#334155' }}>
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        )}
        <button type="button" onClick={onCancel} style={{ ...s.btn, background: '#1e293b', border: '1px solid #334155' }}>Cancel</button>
      </div>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SupportConfig() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | 'new' | connector doc
  const [stats, setStats] = useState({ byStatus: {}, byPriority: {}, recentOpen: [] });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [{ data: connData }, { data: statsData }] = await Promise.all([
        api.get('/api/support-db'),
        api.get('/api/tickets/stats'),
      ]);
      setConnectors(connData.connectors);
      setStats(statsData);
    } catch {
      setConnectors([]);
    }
    setLoading(false);
  }

  async function deleteConnector(id) {
    if (!window.confirm('Remove this connector? The bot will lose access to this database.')) return;
    try {
      await api.delete(`/api/support-db/${id}`);
      setConnectors(c => c.filter(x => x._id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  async function toggleEnabled(conn) {
    try {
      const { data } = await api.put(`/api/support-db/${conn._id}`, { enabled: !conn.enabled });
      setConnectors(c => c.map(x => x._id === conn._id ? data.connector : x));
    } catch (err) {
      alert(err.message);
    }
  }

  function handleSaved(connector) {
    setConnectors(prev => {
      const idx = prev.findIndex(c => c._id === connector._id);
      if (idx >= 0) {
        const next = [...prev]; next[idx] = connector; return next;
      }
      return [...prev, connector];
    });
    setEditing(null);
  }

  const statusMap = stats.byStatus || {};
  const openCount     = statusMap.open || 0;
  const inProgCount   = statusMap.in_progress || 0;
  const resolvedCount = statusMap.resolved || 0;
  const closedCount   = statusMap.closed || 0;
  const totalOpen     = openCount + inProgCount;

  return (
    <div style={s.page}>
      <h1 style={s.title}>Support Configuration</h1>

      {/* Metrics */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Ticket Overview</div>
        <div style={s.metricRow}>
          {[
            { label: 'Open',       val: openCount,     color: '#6366f1' },
            { label: 'In Progress',val: inProgCount,   color: '#f97316' },
            { label: 'Resolved',   val: resolvedCount, color: '#22c55e' },
            { label: 'Closed',     val: closedCount,   color: '#475569' },
          ].map(m => (
            <div key={m.label} style={{ ...s.metricCard, borderColor: m.color + '40' }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: m.color }}>{m.val}</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{m.label}</div>
            </div>
          ))}
        </div>

        {stats.recentOpen?.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, fontWeight: 700 }}>
              Recent Open Tickets ({totalOpen} unresolved)
            </div>
            {stats.recentOpen.map(t => (
              <div key={t._id} style={s.recentTicket}>
                <span style={{ color: '#64748b', fontSize: 11 }}>#{t.ticketNumber}</span>
                <span style={{ color: '#f1f5f9', fontSize: 13, flex: 1 }}>{t.subject}</span>
                <span style={{ color: '#94a3b8', fontSize: 11 }}>{t.learnerName || formatPhone(t.phone)}</span>
                <span style={{ fontSize: 11, color: t.priority === 'urgent' ? '#ef4444' : '#f97316' }}>{t.priority}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DB Connectors */}
      <div style={s.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={s.sectionTitle}>Database Connectors</div>
          {editing !== 'new' && (
            <button onClick={() => setEditing('new')} style={{ ...s.btn, background: '#3b82f6' }}>
              + Add Connector
            </button>
          )}
        </div>

        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          Connect external PostgreSQL or MongoDB databases. The support bot will query these before creating a ticket.
        </div>

        {editing === 'new' && (
          <div style={s.formCard}>
            <div style={s.sectionTitle}>New Connector</div>
            <ConnectorForm onSave={handleSaved} onCancel={() => setEditing(null)} />
          </div>
        )}

        {loading ? (
          <div style={{ color: '#64748b', padding: 20, textAlign: 'center' }}>Loading...</div>
        ) : connectors.length === 0 && editing !== 'new' ? (
          <div style={{ color: '#475569', padding: '30px 0', textAlign: 'center' }}>
            No connectors configured. Add one so the bot can look up learner data before escalating.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {connectors.map(conn => (
              <div key={conn._id} style={s.connectorCard}>
                {editing && editing._id === conn._id ? (
                  <ConnectorForm initial={conn} onSave={handleSaved} onCancel={() => setEditing(null)} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>{conn.label}</span>
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: '#334155', color: '#94a3b8', textTransform: 'uppercase' }}>
                          {conn.type}
                        </span>
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, background: conn.enabled ? '#052e1620' : '#1e293b', color: conn.enabled ? '#22c55e' : '#475569' }}>
                          {conn.enabled ? 'Active' : 'Disabled'}
                        </span>
                        {conn.lastTestStatus && (
                          <span style={{ fontSize: 11, color: conn.lastTestStatus === 'ok' ? '#22c55e' : '#ef4444' }}>
                            Last test: {conn.lastTestStatus}
                          </span>
                        )}
                      </div>
                      {conn.type === 'postgres' && (
                        <div style={{ fontSize: 12, color: '#64748b' }}>{conn.host}:{conn.port || 5432}/{conn.database}</div>
                      )}
                      {conn.description && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{conn.description}</div>
                      )}
                      {conn.allowedTables?.length > 0 && (
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                          Tables: {conn.allowedTables.join(', ')}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => toggleEnabled(conn)} style={{ ...s.btn, background: conn.enabled ? '#334155' : '#1e3a1e', fontSize: 12, padding: '5px 10px' }}>
                        {conn.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => setEditing(conn)} style={{ ...s.btn, background: '#334155', fontSize: 12, padding: '5px 10px' }}>
                        Edit
                      </button>
                      <button onClick={() => deleteConnector(conn._id)} style={{ ...s.btn, background: '#3f0e0e', fontSize: 12, padding: '5px 10px', color: '#f87171' }}>
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page:         { flex: 1, padding: '24px 28px', overflowY: 'auto', background: '#0f172a', color: '#f1f5f9' },
  title:        { fontSize: 22, fontWeight: 700, color: '#f1f5f9', marginBottom: 24 },
  section:      { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 },
  metricRow:    { display: 'flex', gap: 12, flexWrap: 'wrap' },
  metricCard:   { flex: 1, minWidth: 110, background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '14px 16px', textAlign: 'center' },
  recentTicket: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#0f172a', borderRadius: 7, marginBottom: 6 },
  connectorCard: { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: '14px 16px' },
  formCard:     { background: '#0f172a', border: '1px solid #3b82f620', borderRadius: 8, padding: '16px', marginBottom: 16 },
  connectorForm: { display: 'flex', flexDirection: 'column' },
  formRow:      { display: 'flex', gap: 12, marginBottom: 12 },
  label:        { display: 'block', fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' },
  input:        { width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' },
  btn:          { padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#334155', border: 'none', color: '#f1f5f9', whiteSpace: 'nowrap' },
};
